import React, { useState, useEffect, useCallback } from 'react';
import { FormHandles, SubmitHandler } from '@unform/core';
import { Form } from '@unform/web';

import { Link, useNavigate } from 'react-router-dom';

import Slider from '@mui/material/Slider';
import Input from '../../../../components/FormInput';
import Select from '../../../../components/SelectForm';
import Button from '../../../../components/Button';

import { Container, DivCombobox } from './styles';

import api from '../../../../services/api';

interface Customer {
  customerId: number;
  name: string;
}

interface Header {
  id: number;
  filePath: string;
}

interface Background {
  id: number;
  filePath: string;
}

interface Style {
  urlFavicon: string;
  browserTitle: string;
  buttonsColor: string;
  buttonStyle: string;
  buttonPosition: string;
  surveyBoxSize: number;
  overlayBoxColor: string;
  boxQuestionType: string;
  fontFamily: string;
  urlLogo: string;
  urlRadarChart: string;
  headerColor: string;
  heightLogo: string;
  headerImagePath?: string;
  headerImageId?: number;
  header: Header;
  hideProgressText: boolean;
  showConfettiRain: boolean;
  background: Background;
  backgroundImagePath?: string;
  backgroundImageId?: number;
}
interface Survey {
  name: string;
  style: Style;
}

interface Props {
  formRef: React.Ref<FormHandles>;
  buttonText: string;
  initialData?: Survey;
  handleSubmit: SubmitHandler;
  customerData?: Customer;
}

interface SelectProps {
  value: number;
  label: string;
}

interface SelectPropsButtonStyles {
  value: number;
  label: string;
  style: any;
}

interface SelectPropsFontFamily {
  value: string;
  label: string;
}

const FormStyle: React.FC<Props> = props => {
  const [styles, setStyles] = useState<SelectProps[]>();
  const [browserTitle, setBrowserTitle] = useState<string>('');
  const [hideProgressText, setHideProgressText] = useState<boolean>(false);
  const [showConfettiRain, setShowConfettiRain] = useState<boolean>(false);
  const [urlFavicon, setUrlFavicon] = useState<string>('');
  const [buttonsColor, setButtonsColor] = useState<string>('#b31c1d');
  const [overlayBoxColor, setOverlayBoxColor] = useState<string>('#b31c1d');
  const [buttonStyle, setButtonStyle] = useState<string>('');
  const [buttonPosition, setButtonPosition] = useState<string>('');
  const [urlLogo, setUrlLogo] = useState<string>('');
  const [urlRadarChart, setUrlRadarChart] = useState<string>('');
  const [headerColor, setHeaderColor] = useState<string>('#b31c1d');
  const [heightLogo, setHeightLogo] = useState<string>('');
  const [headerImagePath, setHeaderImagePath] = useState<string | undefined>(
    '',
  );

  const fontFamiliesList = ['Roboto', 'GenosGFG'];

  const [fontFamily, setFontFamily] = useState<string>('Roboto');
  const [fontFamilyOptions] = useState<SelectPropsFontFamily[]>(
    fontFamiliesList.map(font => ({ value: font, label: font })),
  );

  const [backgroundImagePath, setBackgroundImagePath] = useState<
    string | undefined
  >('');

  const [images, setImages] = useState<SelectProps[]>();
  const [buttonStylesList, setButtonStylesList] = useState<
    SelectPropsButtonStyles[]
  >([
    {
      value: 1,
      label: 'Old style',
      style: {
        backgroundColor: '#5e5a5875',
      },
    },
    {
      value: 2,
      label: 'Diagonal rounded borders',
      style: {
        backgroundColor: '#5e5a5875',
        borderBottomLeftRadius: '10px',
        borderBottomRightRadius: '0',
        borderTopRightRadius: '10px',
        borderTopLeftRadius: '0',
      },
    },
  ]);
  const [buttonPositionList] = useState([
    {
      value: 'left',
      label: 'Left',
    },
    {
      value: 'right',
      label: 'Right',
    },
  ]);
  const [headerIsColor, setHeaderIsColor] = useState<boolean>(true);
  const [headerType, setHeaderType] = useState<SelectProps[]>();
  const [boxQuestionTypeList, setBoxQuestionTypeList] = useState<SelectProps[]>(
    [
      {
        value: 1,
        label: 'Box with left color line',
      },
      {
        value: 2,
        label: 'No Box',
      },
    ],
  );
  const [boxQuestionType, setBoxQuestionType] = useState<string>('');

  const maxSurveyBoxSize = 95;
  const minSurveyBoxSize = 45;
  const marks = [
    {
      value: minSurveyBoxSize,
      label: '45%',
    },
    {
      value: maxSurveyBoxSize,
      label: '95%',
    },
  ];
  const [surveyBoxSize, setSurveyBoxSize] = useState<number>(maxSurveyBoxSize);
  const navigate = useNavigate();

  useEffect(() => {
    if (props.initialData && props.initialData.style) {
      setBrowserTitle(props.initialData.style.browserTitle);
      setUrlFavicon(props.initialData.style.urlFavicon);
      setButtonsColor(props.initialData.style.buttonsColor);
      setButtonStyle(props.initialData.style.buttonStyle);
      setButtonPosition(props.initialData.style.buttonPosition);
      setSurveyBoxSize(
        props.initialData.style.surveyBoxSize ?? maxSurveyBoxSize,
      );
      setOverlayBoxColor(props.initialData.style.overlayBoxColor);
      setBoxQuestionType(props.initialData.style.boxQuestionType);
      setFontFamily(props.initialData.style.fontFamily || 'Roboto');
      setUrlLogo(props.initialData.style.urlLogo);
      setUrlRadarChart(props.initialData.style.urlRadarChart);
      setHeaderColor(props.initialData.style.headerColor);
      setHeightLogo(props.initialData?.style.heightLogo);
      setHideProgressText(props.initialData.style?.hideProgressText ?? false);
      setShowConfettiRain(props.initialData.style?.showConfettiRain ?? false);

      const formRefLocal = props.formRef as React.MutableRefObject<FormHandles>;
      if (formRefLocal?.current) {
        if (props.initialData?.style.header.filePath) {
          setHeaderImagePath(props.initialData.style.header?.filePath);
          setHeaderIsColor(false);

          if (formRefLocal) {
            const formHandles: any = formRefLocal;
            formHandles.current?.setFieldValue('listHeadertype', {
              value: 2,
              label: 'Header with image',
            });
          }
        } else {
          setHeaderIsColor(true);
        }

        formRefLocal.current?.setFieldValue(
          'buttonStyle',
          buttonStylesList?.filter(
            (bs: any) =>
              bs.value.toString() === props.initialData?.style.buttonStyle,
          )[0],
        );

        formRefLocal.current?.setFieldValue(
          'buttonPosition',
          buttonPositionList?.filter(
            (bs: any) =>
              bs.value.toString() === props.initialData?.style.buttonPosition,
          )[0],
        );

        formRefLocal.current?.setFieldValue(
          'boxQuestionType',
          boxQuestionTypeList?.filter(
            (bs: any) =>
              bs.value.toString() === props.initialData?.style.boxQuestionType,
          )[0],
        );

        formRefLocal.current?.setFieldValue(
          'fontFamily',
          fontFamilyOptions?.filter(
            fo =>
              fo.value.toString() ===
              (props.initialData?.style.fontFamily || 'Roboto'),
          )[0],
        );

        if (props.initialData?.style?.background?.filePath) {
          setBackgroundImagePath(props.initialData.style.background.filePath);
        }
      } else {
        console.warn('Form ref não está pronta para setFieldValue');
      }
    }

    const stylesDefault: any = [
      {
        value: 1,
        label: 'Selbsttest TSÖ',
      },
      {
        value: 2,
        label: 'Emotion Banking',
      },
      {
        value: 3,
        label: 'Spirit Hoch Drei',
      },
      {
        value: 4,
        label: 'Kunde21',
      },
    ];
    setStyles(stylesDefault);

    const HeaderTypeOptions: any = [
      {
        value: 1,
        label: 'Header with color',
      },
      {
        value: 2,
        label: 'Header with image',
      },
    ];

    api.get('images').then(response => {
      setImages(
        response.data.map((img: any) => {
          return {
            value: img.logoId,
            label: img.originalname,
            filePath: `${response.request?.responseURL.replace(
              '/images',
              '/files',
            )}/${img?.filename}`,
          };
        }),
      );
    });

    setHeaderType(HeaderTypeOptions);
  }, [
    boxQuestionTypeList,
    buttonStylesList,
    buttonPositionList,
    fontFamilyOptions,
    props.formRef,
    props.initialData,
  ]);

  const handleBrowserTitleChange = useCallback(x => {
    setBrowserTitle(x.target.value);
  }, []);
  const handleUrlFaviconChange = useCallback(x => {
    setUrlFavicon(x.target.value);
  }, []);
  const handleButtonsColorChange = useCallback(x => {
    setButtonsColor(x.target.value);
  }, []);
  const handleOverlayBoxColorChange = useCallback(x => {
    setOverlayBoxColor(x.target.value);
  }, []);
  const handleButtonStyleChange = useCallback(x => {
    if (x?.value) {
      setButtonStyle(x.value);
    } else {
      setButtonStyle('');
    }
  }, []);
  const handleButtonPositionChange = useCallback(x => {
    if (x?.value) {
      setButtonPosition(x.value);
    } else {
      setButtonPosition('');
    }
  }, []);
  const handleUrlLogoChange = useCallback(x => {
    setUrlLogo(x.target.value);
  }, []);

  const handleUrlRadarChartChange = useCallback(x => {
    setUrlRadarChart(x.target.value);
  }, []);

  const handleHeaderColorChange = useCallback(x => {
    setHeaderColor(x.target.value);
  }, []);

  const handleHeightLogoChange = useCallback(x => {
    setHeightLogo(x.target.value);
  }, []);

  const handleHeaderImageChange = useCallback(x => {
    setHeaderImagePath(x.filePath);
  }, []);

  const handleBackgroundImageChange = useCallback(x => {
    if (x?.filePath) {
      setBackgroundImagePath(x.filePath);
    } else {
      setBackgroundImagePath('');
    }
  }, []);

  const handleChangeHideProgressText = useCallback(() => {
    setHideProgressText(!hideProgressText);
  }, [hideProgressText]);

  const handleSurveysurveyBoxSize = useCallback((x: any) => {
    setSurveyBoxSize(x.target.value);
  }, []);

  const handleChangeShowConfettiRain = useCallback(() => {
    setShowConfettiRain(!showConfettiRain);
  }, [showConfettiRain]);

  const handleHeaderTypeChange = useCallback(x => {
    if (x.value === 1) {
      setHeaderImagePath('');
      setHeaderIsColor(true);
    }

    if (x.value === 2) {
      setHeaderIsColor(false);
      setHeaderImagePath('');
    }
  }, []);

  const handleBoxQuestionTypeChange = useCallback(x => {
    if (x?.value) {
      setBoxQuestionType(x.value);
    } else {
      setBoxQuestionType('');
    }
  }, []);

  const handleFontFamilyChange = useCallback(x => {
    if (x?.value) {
      setFontFamily(x.value);
    } else {
      setFontFamily('');
    }
  }, []);

  const handleStylesChange = useCallback(
    selectedValue => {
      const prefix = process.env.NODE_ENV === 'development' ? 'http' : 'https';

      const serverFavicon = `${prefix}://${window.location.host}`;

      const server = `${serverFavicon}/files`;

      switch (selectedValue.value) {
        case 1: {
          setBrowserTitle('Selbsttest TSÖ');
          setUrlFavicon(`${serverFavicon}/favicontso.ico`);
          setUrlRadarChart('/static/media/bg-radar-chart-pink.9da4933a.png');
          setOverlayBoxColor('#b31c1d');
          setButtonsColor('#b31c1d');
          setHeaderColor('#b31c1d');
          setHeightLogo('100%');

          if (server.includes('localhost') || server.includes('homolog')) {
            const pathLogo = `${server}/f357c64e1ff3a5786bc4-Logo%20TS%C3%96%20transparenter%20HG.png`;
            setUrlLogo(pathLogo);
            const logoDefault = {
              value: 38,
              label: pathLogo,
              filePath: pathLogo,
            };

            const formRefLocal: React.Ref<FormHandles> = props.formRef;
            if (formRefLocal) {
              const formHandles: any = formRefLocal;
              formHandles.current?.setFieldValue('logoId', logoDefault);
            }
          } else {
            const pathLogo = `${server}/203e540f5dd20809256c-Logo%20TS%C3%96%20transparenter%20HG.png`;
            setUrlLogo(pathLogo);
            const logoDefault = {
              value: 41,
              label: pathLogo,
              filePath: pathLogo,
            };
            const formRefLocal: React.Ref<FormHandles> = props.formRef;
            if (formRefLocal) {
              const formHandles: any = formRefLocal;
              formHandles.current?.setFieldValue('logoId', logoDefault);
            }
          }

          break;
        }
        case 2: {
          setBrowserTitle('Emotion Banking');
          setUrlFavicon(`${serverFavicon}/favicon.ico`);
          setUrlRadarChart('/static/media/bg-radar-chart-pink.9da4933a.png');
          setButtonsColor('#FF780C');
          setOverlayBoxColor('#FF780C');
          setHeaderColor('#FF780C');
          setHeightLogo('100%');

          if (server.includes('localhost') || server.includes('homolog')) {
            const pathLogo = `${server}/02f10d4fd7e0b20493ef-1_Logo%20ohne%20Unterschrift.png`;
            setUrlLogo(pathLogo);
            const logoDefault = {
              value: 34,
              label: pathLogo,
              filePath: pathLogo,
            };
            const formRefLocal: React.Ref<FormHandles> = props.formRef;
            if (formRefLocal) {
              const formHandles: any = formRefLocal;
              formHandles.current?.setFieldValue('logoId', logoDefault);
            }
          } else {
            const pathLogo = `${server}/c24a8a5d2b870b93865e-1_Logo%20ohne%20Unterschrift.png`;
            setUrlLogo(pathLogo);
            const logoDefault = {
              value: 44,
              label: pathLogo,
              filePath: pathLogo,
            };
            const formRefLocal: React.Ref<FormHandles> = props.formRef;
            if (formRefLocal) {
              const formHandles: any = formRefLocal;
              formHandles.current?.setFieldValue('logoId', logoDefault);
            }
          }

          break;
        }
        case 3: {
          setBrowserTitle('Spirit Hoch');
          setUrlFavicon(`${serverFavicon}/faviconsp3.ico`);
          setUrlRadarChart('/static/media/bg-radar-chart-pink.9da4933a.png');
          setButtonsColor('#ed1e79');
          setOverlayBoxColor('#ed1e79');
          setHeaderColor('#ed1e79');
          setHeightLogo('100%');

          if (server.includes('localhost') || server.includes('homolog')) {
            const pathLogo = `${server}/529a8b0ac5e562ad7b3c-spirit-hoch.webp`;
            setUrlLogo(pathLogo);
            const logoDefault = {
              value: 30,
              label: pathLogo,
              filePath: pathLogo,
            };
            const formRefLocal: React.Ref<FormHandles> = props.formRef;
            if (formRefLocal) {
              const formHandles: any = formRefLocal;
              formHandles.current?.setFieldValue('logoId', logoDefault);
            }
          } else {
            const pathLogo = `${server}/529a8b0ac5e562ad7b3c-spirit-hoch.webp`;
            setUrlLogo(pathLogo);
            const logoDefault = {
              value: 30,
              label: pathLogo,
              filePath: pathLogo,
            };
            const formRefLocal: React.Ref<FormHandles> = props.formRef;
            if (formRefLocal) {
              const formHandles: any = formRefLocal;
              formHandles.current?.setFieldValue('logoId', logoDefault);
            }
          }

          break;
        }
        case 4: {
          setBrowserTitle('Kunde 21');
          setUrlFavicon(`${serverFavicon}/favicon-kunde21.ico`);
          setUrlRadarChart('/static/media/bg-radar-chart-pink.9da4933a.png');
          setButtonsColor('#00A19A');
          setOverlayBoxColor('#00A19A');
          setHeaderColor('#00A19A');
          setHeightLogo('100%');

          if (server.includes('localhost') || server.includes('homolog')) {
            const pathLogo = `${server}/5976efd805a020836612-logo-kunde21.png`;
            setUrlLogo(pathLogo);
            const logoDefault = {
              value: 41,
              label: pathLogo,
              filePath: pathLogo,
            };
            const formRefLocal: React.Ref<FormHandles> = props.formRef;
            if (formRefLocal) {
              const formHandles: any = formRefLocal;
              formHandles.current?.setFieldValue('logoId', logoDefault);
            }
          } else {
            const pathLogo = `${server}/881b5bb3790f53126ec9-K21%20wei%C3%9F.png`;
            setUrlLogo(pathLogo);
            const logoDefault = {
              value: 54,
              label: pathLogo,
              filePath: pathLogo,
            };
            const formRefLocal: React.Ref<FormHandles> = props.formRef;
            if (formRefLocal) {
              const formHandles: any = formRefLocal;
              formHandles.current?.setFieldValue('logoId', logoDefault);
            }
          }

          break;
        }
        default: {
          setBrowserTitle('Victor Neu');
          setUrlFavicon(`${serverFavicon}favicon.ico`);
          setUrlRadarChart('/static/media/bg-radar-chart-pink.9da4933a.png');
          setButtonsColor('#1ab394');
          setOverlayBoxColor('#1ab394');

          setHeaderColor('#1ab394');

          setHeightLogo('100%');

          if (server.includes('localhost') || server.includes('homolog')) {
            const pathLogo = `${server}/02f10d4fd7e0b20493ef-1_Logo%20ohne%20Unterschrift.png`;
            setUrlLogo(pathLogo);
            const logoDefault = {
              value: 34,
              label: pathLogo,
              filePath: pathLogo,
            };
            const formRefLocal: React.Ref<FormHandles> = props.formRef;
            if (formRefLocal) {
              const formHandles: any = formRefLocal;
              formHandles.current?.setFieldValue('logoId', logoDefault);
            }
          } else {
            const pathLogo = `${server}/c24a8a5d2b870b93865e-1_Logo%20ohne%20Unterschrift.png`;
            setUrlLogo(pathLogo);
            const logoDefault = {
              value: 44,
              label: pathLogo,
              filePath: pathLogo,
            };
            const formRefLocal: React.Ref<FormHandles> = props.formRef;
            if (formRefLocal) {
              const formHandles: any = formRefLocal;
              formHandles.current?.setFieldValue('logoId', logoDefault);
            }
          }
          break;
        }
      }
    },
    [props.formRef],
  );

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return (
    <Container>
      <Form
        ref={props.formRef}
        initialData={props.initialData}
        onSubmit={props.handleSubmit}
      >
        <h2>Default Styles</h2>
        <Select
          name="listStylesDefault"
          options={styles}
          defaultValue={{ label: 'Emotion Banking', value: 2 }}
          onChange={e => handleStylesChange(e)}
        />

        <h2>Survey Box Size</h2>
        <Slider
          style={{ width: '99%', marginLeft: '8px' }}
          name="surveyBoxSize"
          marks={marks}
          value={surveyBoxSize}
          min={minSurveyBoxSize}
          max={maxSurveyBoxSize}
          valueLabelDisplay="auto"
          onChange={e => handleSurveysurveyBoxSize(e)}
        />

        <h2>Browser Title</h2>
        <Input
          type="text"
          name="browserTitle"
          value={browserTitle}
          placeholder="Enter title to display in browser tab"
          onChange={handleBrowserTitleChange}
        />

        <h2>Box Questions Type</h2>
        <Select
          name="boxQuestionType"
          options={boxQuestionTypeList}
          onChange={e => handleBoxQuestionTypeChange(e)}
        />

        <h2>Font Family</h2>
        <Select
          isClearable={true}
          name="fontFamily"
          options={fontFamilyOptions}
          defaultValue={fontFamilyOptions.find(font => font.value === 'Roboto')}
          onChange={e => handleFontFamilyChange(e)}
          formatOptionLabel={(font: SelectPropsFontFamily) => (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
              className="header-option"
            >
              <span>{font.label}</span>
              <span style={{ fontFamily: font.value }}>Aa Bb Cc 123</span>
            </div>
          )}
        />

        <h2>Buttons color</h2>
        <Input
          type="color"
          name="buttonsColor"
          value={buttonsColor}
          onChange={handleButtonsColorChange}
        />

        <h2>Button style</h2>
        <Select
          isClearable={true}
          name="buttonStyle"
          options={buttonStylesList}
          onChange={e => handleButtonStyleChange(e)}
          formatOptionLabel={bStyle => (
            <div
              style={{ display: 'flex', alignItems: 'center' }}
              id="header-option"
              className="header-option"
            >
              <input
                id="input-button"
                className="sv-btn sv-btn--navigation sv-footer__prev-btn"
                type="button"
                value="Zurück"
                style={bStyle.style}
              />
              <span id="span"> {bStyle.label} </span>
            </div>
          )}
        />

        <h2>Button position</h2>
        <Select
          isClearable={true}
          name="buttonPosition"
          options={buttonPositionList}
          value={
            buttonPositionList.find((o: any) => o.value === buttonPosition) ||
            null
          }
          onChange={e => handleButtonPositionChange(e)}
        />

        <h2>Background Image</h2>
        <Select
          isClearable={true}
          name="backgroundImageId"
          options={images}
          onChange={e => handleBackgroundImageChange(e)}
          formatOptionLabel={img => (
            <div className="header-option">
              <img style={{ maxWidth: '45px' }} src={img.filePath} alt="img" />
              <span> {img.label} </span>
            </div>
          )}
        />

        <h2>OverlayBox color</h2>
        <Input
          type="color"
          name="overlayBoxColor"
          value={overlayBoxColor}
          onChange={handleOverlayBoxColorChange}
        />

        <h2>Header</h2>
        <Select
          name="listHeadertype"
          options={headerType}
          defaultValue={{ label: 'Header with color', value: 1 }}
          onChange={e => handleHeaderTypeChange(e)}
        />

        <div hidden={headerIsColor}>
          <h2>Header Image</h2>
          <Select
            name="headerImageId"
            options={images}
            onChange={e => handleHeaderImageChange(e)}
            formatOptionLabel={img => (
              <div className="header-option">
                <img
                  style={{ maxWidth: '45px' }}
                  src={img.filePath}
                  alt="img"
                />
                <span>{img.label}</span>
              </div>
            )}
          />
        </div>

        <div hidden={!headerIsColor}>
          <h2>Header color</h2>
          <Input
            type="color"
            name="headerColor"
            value={headerColor}
            onChange={handleHeaderColorChange}
          />

          <h2>Size Logo </h2>
          <Input
            type="text"
            name="heightLogo"
            placeholder="Enter height logo"
            value={heightLogo}
            onChange={handleHeightLogoChange}
          />
        </div>

        <div hidden={true}>
          <h2>Url Favicon</h2>
          <Input
            type="text"
            name="urlFavicon"
            placeholder="Enter url to display in browser tab"
            value={urlFavicon}
            onChange={handleUrlFaviconChange}
          />

          <h2>Box question type</h2>
          <Input
            type="text"
            name="boxQuestionType"
            value={boxQuestionType}
            onChange={handleBoxQuestionTypeChange}
          />

          <h2>font family</h2>
          <Input
            type="text"
            name="fontFamily"
            value={fontFamily}
            onChange={handleFontFamilyChange}
          />

          <h2>button style</h2>
          <Input
            type="text"
            name="buttonStyle"
            placeholder="Enter url to display in browser tab"
            value={buttonStyle}
            onChange={handleButtonStyleChange}
          />

          <h2>button position</h2>
          <Input
            type="text"
            name="buttonPosition"
            placeholder="Enter url to display in browser tab"
            value={buttonPosition}
            onChange={handleButtonPositionChange}
          />

          <h2>survey box size</h2>
          <Input
            type="text"
            name="surveyBoxSize"
            placeholder="Enter url to display in browser tab"
            value={surveyBoxSize}
            onChange={handleSurveysurveyBoxSize}
          />

          <h2>Url Logo</h2>
          <Input
            type="text"
            name="urlLogo"
            value={urlLogo}
            placeholder="Enter url logo to display in surveys"
            onChange={handleUrlLogoChange}
          />

          <h2>Url Image</h2>
          <Input
            type="text"
            name="headerImagePath"
            value={headerImagePath}
            placeholder="Enter url image to display in surveys"
            onChange={handleHeaderImageChange}
          />

          <h2>Url Image background</h2>
          <Input
            type="text"
            name="backgroundImagePath"
            value={backgroundImagePath}
            placeholder="Enter url image to display in surveys"
            onChange={handleBackgroundImageChange}
          />

          <h2>Url Radar Chart</h2>
          <Input
            type="text"
            name="urlRadarChart"
            value={urlRadarChart}
            placeholder="Enter url logo to display in surveys"
            onChange={handleUrlRadarChartChange}
          />
        </div>

        <DivCombobox>
          <Input
            id="cbProgressText"
            type="checkbox"
            checked={hideProgressText}
            onChange={handleChangeHideProgressText}
            name="hideProgressText"
            value={String(hideProgressText)}
            styled={false}
          />
          <span id="labelHideProgressText">Hide Progress text</span>

          <br />

          <Input
            id="cbShowConfettiRain"
            type="checkbox"
            checked={showConfettiRain}
            onChange={handleChangeShowConfettiRain}
            name="showConfettiRain"
            value={String(showConfettiRain)}
            styled={false}
          />
          <span id="labelShowConfettiRain">Show confetti rain when finish</span>
        </DivCombobox>

        <Button $width="120px" $height="40px" $marginRight="30px" type="submit">
          Confirm
        </Button>

        <Link to={''} onClick={handleBack}>
          <Button $width="120px" $height="40px">
            Back
          </Button>
        </Link>
      </Form>
    </Container>
  );
};

export default FormStyle;
